import { useState, Fragment } from "react";
import { Card, Table, TableWrapper, Mono, Badge } from "../../components/ui";
import { HexDisplay } from "../../components/HexDisplay";
import { theme } from "../../lib/theme";
import { CalldataCell, CalldataToggle } from "./styled";

export function PublicCallsSection({
  calls,
  labeledAddresses,
}: {
  calls: {
    phase: string;
    contractAddress: string;
    functionSelector: string | null;
    msgSender: string | null;
    calldataSize: number;
    calldata: string[];
    isStaticCall: boolean;
    label: string | null;
    contractType: string | null;
  }[];
  labeledAddresses: Set<string>;
}) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggle = (i: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <Card
      style={{ padding: 0, overflow: "hidden", marginBottom: theme.spacing.md }}
    >
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>Phase</th>
              <th>Contract</th>
              <th>Selector</th>
              <th>Sender</th>
              <th>Calldata</th>
              <th>Static</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c, i) => {
              const phaseColor =
                c.phase === "setup"
                  ? theme.colors.accent
                  : c.phase === "teardown"
                    ? theme.colors.warning
                    : theme.colors.primary;
              const hasCalldata = c.calldata.length > 0;
              const isExpanded = expandedRows.has(i);
              return (
                <Fragment key={i}>
                  <tr>
                    <td>
                      <Badge color={phaseColor}>{c.phase}</Badge>
                    </td>
                    <td>
                      <HexDisplay
                        address={c.contractAddress}
                      />
                    </td>
                    <td>
                      <Mono style={{ fontSize: "10px" }}>
                        {c.functionSelector ?? "\u2014"}
                      </Mono>
                    </td>
                    <td>
                      {c.msgSender ? (
                        <HexDisplay
                          address={c.msgSender}
                        />
                      ) : (
                        "\u2014"
                      )}
                    </td>
                    <td>
                      {hasCalldata ? (
                        <CalldataToggle onClick={() => toggle(i)}>
                          {c.calldataSize} fields {isExpanded ? "▾" : "▸"}
                        </CalldataToggle>
                      ) : (
                        <span style={{ fontSize: theme.fontSize.xs }}>
                          {c.calldataSize} fields
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: theme.fontSize.xs }}>
                        {c.isStaticCall ? "true" : "false"}
                      </span>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <CalldataCell colSpan={6}>
                        <div style={{ maxHeight: "160px", overflowY: "auto" }}>
                          {c.calldata.map((field, j) => {
                            const isLabeled = labeledAddresses.has(
                              field.toLowerCase(),
                            );
                            return (
                              <div
                                key={j}
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "baseline",
                                }}
                              >
                                <span
                                  style={{
                                    color: theme.colors.textMuted,
                                    fontSize: "10px",
                                    minWidth: "20px",
                                    textAlign: "right",
                                  }}
                                >
                                  {j}
                                </span>
                                <Mono
                                  style={{
                                    fontSize: "10px",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {isLabeled && (
                                    <>
                                      <Badge
                                        color={theme.colors.accent}
                                        style={{ fontSize: "9px" }}
                                      >
                                        addr?
                                      </Badge>{" "}
                                    </>
                                  )}
                                  <HexDisplay address={field} abbreviate={false} />
                                </Mono>
                              </div>
                            );
                          })}
                        </div>
                      </CalldataCell>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>
    </Card>
  );
}
